import { getHelpCenter, getCollection, getArticle, searchHelpCenter, viewArticle, reactArticle, startChatFromArticle } from "../../api/helpcenter";
import { getCategories, getContents, rateContents, increaseViewCatalog, searchCatalog } from "../../api/catalog";
import { toObject } from "../../utils/languageContent";

// Ziyaretcinin dilinde icerik yoksa varsayilan dil girdisi tum site dillerine kopyalanir
const projectLocales = (rows, ctx) => {
    const list = (rows || []).filter((l) => l && l.languageCode);
    if (!list.length) return list;
    const base = list.find((l) => l.languageCode === ctx.defaultLocale) || list[0];
    const have = new Set(list.map((l) => l.languageCode));
    const missing = (ctx.codes || []).filter((c) => !have.has(c)).map((c) => ({ ...base, languageCode: c }));
    return [...list, ...missing];
};

// Yeni API'yi eski katalog sekline cevirir; V2/Catalog.vue ve catalog/Catalog.vue dokunulmadan calisir
const collectionItem = (c, ctx) =>
    toObject({
        _id: c._id,
        type: "menu",
        parentId: c.parentId || null,
        updatedAt: c.updatedAt || null,
        articleCount: c.articleCount || 0,
        childCount: (c.children || []).length,
        icon: c.icon || null,
        languageContent: projectLocales((c.languageContent || []).map((l) => ({ languageCode: l.languageCode, name: l.name, content: l.description || "" })), ctx),
    });
const articleItem = (a, ctx) =>
    toObject({
        _id: a._id,
        type: "content",
        parentId: a.collectionId,
        slug: a.slug,
        updatedAt: a.updatedAt || a.publishedAt || null,
        views: a.views || 0,
        languageContent: projectLocales((a.languageContent || []).map((l) => ({ languageCode: l.languageCode, name: l.title, content: l.bodyHtml || l.description || "" })), ctx),
    });

const setting = {
    namespaced: true,
    state: {
        content: [],
        isContentLastChild: false,
        enabled: null,
        // yayinda, listelenen en az bir makale var mi; yoksa widget Yardim sekmesini ve makale kartini gostermez
        hasContent: false,
        defaultLocale: null,
        // eski katalog gosteriliyor (sunucu legacy dedi)
        legacy: false,
        // content kok liste mi ve ne zaman yuklendi; Yardim sekmesine her donuste tekrar istek atilmaz
        rootLoadedAt: 0,
    },
    mutations: {
        SET_CATEGORY: (state, payload) => {
            state.content = payload;
            state.isContentLastChild = false;
            state.rootLoadedAt = Date.now();
        },
        SET_CHILDREN: (state, payload) => {
            state.content = payload;
            state.rootLoadedAt = 0;
        },
        SET_IS_LAST_CHILD: (state, payload) => {
            state.isContentLastChild = payload;
        },
        SET_ENABLED: (state, v) => {
            state.enabled = v;
        },
        SET_HAS_CONTENT: (state, v) => {
            state.hasContent = !!v;
        },
        SET_DEFAULT_LOCALE: (state, v) => {
            state.defaultLocale = v;
        },
        SET_LEGACY: (state, v) => {
            state.legacy = !!v;
        },
    },
    getters: {
        // dil projeksiyonu icin varsayilan dil + sitedeki tum dil kodlari
        localeContext: (state, getters, rootState) => ({
            defaultLocale: state.defaultLocale,
            codes: ((rootState.language && rootState.language.languages) || []).map((l) => l.code),
        }),
    },
    actions: {
        getCategories({ commit, dispatch, getters }) {
            return getHelpCenter()
                .then((r) => {
                    // eski katalogu olup yeni bilgi bankasina hic baslamamis ana web chat; karari sunucu verir
                    if (r.data.legacy) return dispatch("loadLegacy");
                    const collections = r.data.collections || [];
                    commit("SET_LEGACY", false);
                    commit("SET_ENABLED", true);
                    commit("SET_HAS_CONTENT", collections.length > 0);
                    commit("SET_DEFAULT_LOCALE", (r.data.helpCenter || {}).defaultLocale || null);
                    commit("SET_CATEGORY", collections.map((c) => collectionItem(c, getters.localeContext)));
                })
                .catch((e) => {
                    if (e.response && e.response.status === 404) {
                        commit("SET_LEGACY", false);
                        commit("SET_ENABLED", false);
                        commit("SET_HAS_CONTENT", false);
                        commit("SET_CATEGORY", []);
                    } else {
                        throw e;
                    }
                });
        },
        // Kok liste yakin zamanda yuklendiyse ve hala ekrandaysa onu kullanir
        ensureRoot({ state, dispatch }, { maxAgeMs = 60000 } = {}) {
            if (state.rootLoadedAt && Date.now() - state.rootLoadedAt < maxAgeMs) return Promise.resolve();
            return dispatch("getCategories");
        },
        // Adresten geri yuklenen koleksiyon: baslik icin koleksiyonun kendisi de doner
        openCollectionById({ commit, getters, state }, id) {
            if (state.legacy) return Promise.resolve(null);
            return getCollection(id).then((r) => {
                commit("SET_CHILDREN", [
                    ...(r.data.children || []).map((c) => collectionItem(c, getters.localeContext)),
                    ...(r.data.articles || []).map((a) => articleItem(a, getters.localeContext)),
                ]);
                return r.data.collection ? collectionItem(r.data.collection, getters.localeContext) : null;
            });
        },
        // eski katalog: yeni bilgi bankasina gecene kadar musterinin icerigi widget'tan kaybolmasin
        loadLegacy({ commit }) {
            return getCategories()
                .then((r) => r.data || [])
                .catch(() => [])
                .then((rows) => {
                    commit("SET_LEGACY", rows.length > 0);
                    commit("SET_ENABLED", rows.length > 0);
                    commit("SET_HAS_CONTENT", false);
                    commit("SET_CATEGORY", rows.map((x) => toObject(x)));
                });
        },
        getChildren({ commit, getters, state }, parentId) {
            if (state.legacy) return getContents(parentId).then((r) => commit("SET_CHILDREN", r.data.map((x) => toObject(x))));
            return getCollection(parentId).then((r) =>
                commit("SET_CHILDREN", [
                    ...(r.data.children || []).map((c) => collectionItem(c, getters.localeContext)),
                    ...(r.data.articles || []).map((a) => articleItem(a, getters.localeContext)),
                ])
            );
        },
        openArticle({ commit, getters, state }, { item, lang }) {
            if (state.legacy) {
                commit("SET_CHILDREN", [item]);
                commit("SET_IS_LAST_CHILD", true);
                return Promise.resolve();
            }
            return getArticle(item._id, lang).then((r) => {
                commit("SET_CHILDREN", [articleItem(r.data, getters.localeContext)]);
                commit("SET_IS_LAST_CHILD", true);
            });
        },
        viewArticle({ state }, { id, lang }) {
            if (state.legacy) return increaseViewCatalog(id).catch(() => {});
            return viewArticle(id, lang).catch(() => {});
        },
        reactArticle({ state }, { id, reaction, lang }) {
            // eski katalogda yalniz begen/begenme var
            if (state.legacy) return reaction === "neutral" ? Promise.resolve() : rateContents({ id, rate: reaction === "happy" ? 1 : 0 }).catch(() => {});
            return reactArticle(id, reaction, lang).catch(() => {});
        },
        startChatFromArticle({ state }, { id, lang }) {
            if (state.legacy) return Promise.resolve();
            return startChatFromArticle(id, lang).catch(() => {});
        },
        rateContent({ dispatch }, { id, rate }) {
            return dispatch("reactArticle", { id, reaction: rate === 1 ? "happy" : "sad" });
        },
        setContent({ commit }, payload) {
            commit("SET_CHILDREN", [payload]);
        },
        setIsLastChild({ commit }, payload) {
            commit("SET_IS_LAST_CHILD", payload);
        },
        searchCatalog({ state }, { query, lang }) {
            if (state.legacy) return searchCatalog(query, lang);
            // arama sonuclari standalone Catalog.vue'da languageContent[0] ile okunur, toObject'e cevirme
            return searchHelpCenter(query, lang).then((r) => ({
                data: (r.data || []).map((a) => ({
                    _id: a._id,
                    type: "content",
                    parentId: a.collectionId,
                    slug: a.slug,
                    languageContent: [{ languageCode: lang, name: a.title, content: a.description || "" }],
                })),
            }));
        },
    },
};

export default setting;



// WEBPACK FOOTER //
// ./src/store/modules/catalog.js